import { Injectable } from '@angular/core';
import { Business, BusinessAddress } from '@models/businesses';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../../base-form-mapper';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	MatterTitle,
} from '@models/matter';
import { format } from 'date-fns';
import { isSet } from '@core/util/value';
import { addressString } from '@core/util/address';
import {
	clientLegalName,
	getSignatories,
	isGuarantor,
} from '@core/util/client';
import { MatterMortgage } from '@models/mortgage';
import { formatCentsToDollars } from '@core/util/money';
import { MortgagePriority } from '@models/encumbrance';
import { GetDateObject } from '@core/util/datetime';
import { TitleInsurancePolicyType } from '@models/title-insurance';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { joinNames } from '@core/util/string';

@Injectable({
	providedIn: 'root',
})
export class FIRST_NATIONAL_FINAL_REPORT_ON_TITLE_Mapper extends BaseFormMapper<FIRST_NATIONAL_FINAL_REPORT_ON_TITLE> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: FIRST_NATIONAL_FINAL_REPORT_ON_TITLE,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgage,
	): FIRST_NATIONAL_FINAL_REPORT_ON_TITLE {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as FIRST_NATIONAL_FINAL_REPORT_ON_TITLE);

		if (mortgage?.referenceNumber && !isSet(_formData?.mortgageNumber)) {
			_formData.mortgageNumber = mortgage.referenceNumber;
		}

		if (matterClients?.length && !isSet(_formData?.mortgagors)) {
			const FilteredMatterClients = matterClients?.filter(
				(client) => !isGuarantor(client),
			);
			const Contacts = getSignatories(FilteredMatterClients);
			_formData.mortgagors = joinNames(
				Contacts.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (mortgage?.guarantors && !isSet(_formData?.guarantors)) {
			const FilteredMatterClients = matterClients?.filter(
				(client) =>
					isGuarantor(client) && mortgage.guarantors.includes(client.id),
			);
			_formData.guarantors = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			matter?.propertyDetails?.address?.street &&
			!isSet(_formData?.civicAddress1)
		) {
			_formData.civicAddress1 = matter.propertyDetails.address.street;
		}

		if (matter?.propertyDetails?.address && !isSet(_formData?.civicAddress2)) {
			_formData.civicAddress2 = addressString(
				matter.propertyDetails.address,
				false,
			);
		}

		if (matter?.closingDate && !isSet(_formData?.dateOfMortgage)) {
			_formData.dateOfMortgage = format(
				GetDateObject(matter.closingDate),
				'MMMM _____, yyyy',
			);
		}

		if (mortgage?.registrationNumber && !isSet(_formData?.registrationNumber)) {
			_formData.registrationNumber = mortgage.registrationNumber;
		}

		if (mortgage?.registrationDate && !isSet(_formData?.registrationDate)) {
			_formData.registrationDate = format(
				GetDateObject(mortgage.registrationDate),
				'MMMM dd, yyyy',
			);
		}

		if (
			matter?.propertyDetails?.lro?.selected &&
			!isSet(_formData?.landRegistryOffice)
		) {
			_formData.landRegistryOffice = matter.propertyDetails.lro.selected.name;
		}

		if (mortgage?.amount && !isSet(_formData?.mortgageAmount)) {
			_formData.mortgageAmount = formatCentsToDollars(mortgage.amount);
		}

		if (
			matter?.fireInsurance?.fireInsuranceCompany?.name &&
			!isSet(_formData?.insurer)
		) {
			_formData.insurer = matter.fireInsurance.fireInsuranceCompany.name;
		}

		if (
			matter?.fireInsurance?.policyNumber &&
			!isSet(_formData?.policyNumber)
		) {
			_formData.policyNumber = matter.fireInsurance.policyNumber;
		}

		if (matter?.fireInsurance?.expiryDate && !isSet(_formData?.expiryDate)) {
			_formData.expiryDate = format(
				GetDateObject(matter.fireInsurance.expiryDate),
				'MMMM dd, yyyy',
			);
		}

		if (
			matter?.titleInsurance?.companyName &&
			!isSet(_formData?.nameOfTitleInsuranceCompany)
		) {
			_formData.nameOfTitleInsuranceCompany = matter.titleInsurance.companyName;
		}

		const lenderPolicy = matter?.titleInsurance?.policies?.find(
			(policy) =>
				policy.type === TitleInsurancePolicyType.LENDER &&
				policy.priority === mortgage?.priority,
		);
		if (lenderPolicy && !isSet(_formData?.certificateNumber)) {
			_formData.certificateNumber = lenderPolicy.number;
		}

		if (mortgage?.amount && !isSet(_formData?.amountOfInsurance)) {
			_formData.amountOfInsurance = formatCentsToDollars(mortgage.amount);
		}

		if (
			matter?.titleInsurance?.effectiveDate &&
			!isSet(_formData?.dateOfPolicy)
		) {
			_formData.dateOfPolicy = format(
				GetDateObject(matter.titleInsurance.effectiveDate),
				'MMMM dd, yyyy',
			);
		}

		if (mortgage?.priority && !isSet(_formData?.priority)) {
			_formData.priority = MortgagePriority.find(
				(option) => mortgage.priority === option.value,
			)?.label;
		}

		if (business?.name && !isSet(_formData?.nameOfFirm)) {
			_formData.nameOfFirm = business.name;
		}

		if (businessAddress?.street && !isSet(_formData?.address)) {
			_formData.address = businessAddress.street;
		}

		if (matter?.propertyDetails?.address && !isSet(_formData?.address2)) {
			_formData.address2 = addressString(matter.propertyDetails.address, false);
		}

		if (businessAddress?.phoneNumber && !isSet(_formData?.telephoneNumber)) {
			_formData.telephoneNumber = businessAddress.phoneNumber;
		}

		if (matter?.lawyerAssigned && !isSet(_formData?.nameOfSolicitor)) {
			_formData.nameOfSolicitor = matter.lawyerAssigned.name;
		}

		if (!isSet(_formData?.date)) {
			_formData.date = format(new Date(), 'MMMM dd, yyyy');
		}

		return _formData;
	}

	MapValues(): Partial<FIRST_NATIONAL_FINAL_REPORT_ON_TITLE> {
		return {};
	}
}

const Mappings: FIRST_NATIONAL_FINAL_REPORT_ON_TITLE = {
	mortgageNumber: 'Mortgage Number',
	mortgagors: 'Mortgagors',
	guarantors: 'Guarantors',
	civicAddress1: 'Civic Address 1',
	civicAddress2: 'Civic Address 2',
	theProperty: 'the Property',
	dateOfMortgage: 'Date of Mortgage',
	registrationNumber: 'Registration Number',
	registrationDate: 'Registration Date',
	landRegistryOffice: 'Land Registry Office',
	mortgageAmount: 'Mortgage Amount',
	insurer: 'Insurer',
	agent: 'Agent',
	policyNumber: 'Policy Number',
	expiryDate: 'Expiry Date',
	nameOfTitleInsuranceCompany: 'Name of Title Insurance Company',
	certificateNumber: 'Certificate Number',
	amountOfInsurance: 'Amount of Insurance',
	dateOfPolicy: 'Date of Policy',
	priority: 'Priority',
	nameOfFirm: 'Name of Firm',
	address: 'Address',
	address2: 'Address_2',
	telephoneNumber: 'Telephone Number',
	signatureOfSolicitor: 'Signature of Solicitor',
	nameOfSolicitor: 'Name of Solicitor',
	date: 'Date',
	text32: 'Text32',
};

export interface FIRST_NATIONAL_FINAL_REPORT_ON_TITLE {
	mortgageNumber: string;
	mortgagors: string;
	guarantors: string;
	civicAddress1: string;
	civicAddress2: string;
	theProperty: string;
	dateOfMortgage: string;
	registrationNumber: string;
	registrationDate: string;
	landRegistryOffice: string;
	mortgageAmount: string;
	insurer: string;
	agent: string;
	policyNumber: string;
	expiryDate: string;
	nameOfTitleInsuranceCompany: string;
	certificateNumber: string;
	amountOfInsurance: string;
	dateOfPolicy: string;
	priority: string;
	nameOfFirm: string;
	address: string;
	address2: string;
	telephoneNumber: string;
	signatureOfSolicitor: string;
	nameOfSolicitor: string;
	date: string;
	text32: string;
}
